import { IconName } from '@ardoq/icons';
import { ViewpointBuilderTabItemMeta } from './types';
import { t, type I18nLocale } from '@ardoq/i18n';

const startComponents = (
  i18nLocale: I18nLocale
): ViewpointBuilderTabItemMeta => ({
  tabId: 'SELECT_CONTEXT_COMPONENT_INSTANCES_TAB',
  label: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.contextComponentType',
    'Context component type'
  ),
  icon: IconName.API,
  description: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.desc.selectContextComponent',
    'Open selected components directly or select references in the step below to see other linked components.'
  ),
});

const contextComponentType = (
  i18nLocale: I18nLocale
): ViewpointBuilderTabItemMeta => ({
  tabId: 'SELECT_CONTEXT_COMPONENT_TYPE_TAB',
  label: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.contextComponentType',
    'Context component type'
  ),
  icon: IconName.COMPONENT,
  description: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.desc.contextComponentType',
    'The Viewpoint is based on this component type. Next, you select references to see other component types'
  ),
});

const subgraph = (i18nLocale: I18nLocale): ViewpointBuilderTabItemMeta => ({
  tabId: 'SUBGRAPH_TAB',
  label: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.componentsAndReferenceTypes',
    'Component and reference types'
  ),
  icon: IconName.ACCOUNT_TREE,
  description: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.desc.componentsAndReferenceTypes',
    'Use references to see linked components. You can select any of the component types here and select references for it.'
  ),
});

const filters = (i18nLocale: I18nLocale): ViewpointBuilderTabItemMeta => ({
  tabId: 'FILTERS_TAB',
  label: t(i18nLocale, 'viewpointBuilder:navigation.tab.filters', 'Filters'),
  icon: IconName.FILTER_LIST,
  description: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.desc.filters',
    'Narrow down and specify the components that appear in the dataset when this Viewpoint is used.'
  ),
});

const collapsePaths = (
  i18nLocale: I18nLocale
): ViewpointBuilderTabItemMeta => ({
  tabId: 'COLLAPSE_PATHS_TAB',
  label: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.collapsePaths',
    'Collapse paths'
  ),
  icon: IconName.COLLAPSE_ALL,
  description: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.desc.collapsePaths',
    'Hide the data between indirectly connected component types.'
  ),
});

const requiredComponents = (
  i18nLocale: I18nLocale
): ViewpointBuilderTabItemMeta => ({
  tabId: 'REQUIRED_COMPONENTS_TAB',
  label: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.requiredPaths',
    'Required paths'
  ),
  icon: IconName.SETTINGS,
  description: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.desc.requiredPaths',
    'Select the paths that the components in your dataset must match.'
  ),
});

const viewpointDetails = (
  i18nLocale: I18nLocale
): ViewpointBuilderTabItemMeta => ({
  tabId: 'META_INFO_TAB',
  label: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.viewpointDetails',
    'Viewpoint details'
  ),
  icon: IconName.DESCRIPTION,
  description: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.desc.viewpointDetails',
    'The name and description helps everyone understand what data can be gotten with this Viewpoint.'
  ),
});

const groups = (i18nLocale: I18nLocale): ViewpointBuilderTabItemMeta => ({
  tabId: 'GROUPS_TAB',
  label: t(i18nLocale, 'viewpointBuilder:navigation.tab.groups', 'Groups'),
  icon: IconName.APPLICATION_GROUP,
  description: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.desc.groups',
    'Show the hierarchy and relationship between components.'
  ),
});

const formatting = (i18nLocale: I18nLocale): ViewpointBuilderTabItemMeta => ({
  tabId: 'FORMATTING_TAB',
  label: t(i18nLocale, 'viewpointBuilder:navigation.tab.format', 'Format'),
  icon: IconName.PALETTE,
  description: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.desc.formatting',
    'Set rules to automatically highlight components and references with colors.'
  ),
});

const labels = (i18nLocale: I18nLocale): ViewpointBuilderTabItemMeta => ({
  tabId: 'LABELS_TAB',
  label: t(i18nLocale, 'viewpointBuilder:navigation.tab.labels', 'Labels'),
  icon: IconName.TAG,
  description: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.desc.labels',
    'Show field values on specific component and reference types. Component name is shown by default.'
  ),
});

export const openDatasetMenuItems = (
  i18nLocale: I18nLocale
): ViewpointBuilderTabItemMeta[] => [
  {
    ...startComponents(i18nLocale),
    label: t(
      i18nLocale,
      'viewpointBuilder:navigation.tab.selectContextComponent',
      'Select context component'
    ),
    icon: IconName.COMPONENT,
  },
  {
    ...subgraph(i18nLocale),
    label: t(
      i18nLocale,
      'viewpointBuilder:navigation.tab.selectReferences',
      'Select references'
    ),
  },
  {
    ...filters(i18nLocale),
    description: t(
      i18nLocale,
      'viewpointBuilder:navigation.tab.desc.addFilters',
      'Select a component or reference type and add filters to it. Use this to narrow down and specify the components that appear in the dataset.'
    ),
    label: t(
      i18nLocale,
      'viewpointBuilder:navigation.tab.addFilters',
      'Add filters'
    ),
  },
  requiredComponents(i18nLocale),
  collapsePaths(i18nLocale),
];

export const editViewpointMenuItems = (
  i18nLocale: I18nLocale
): ViewpointBuilderTabItemMeta[] => [
  viewpointDetails(i18nLocale),
  startComponents(i18nLocale),
  subgraph(i18nLocale),
  filters(i18nLocale),
  requiredComponents(i18nLocale),
  collapsePaths(i18nLocale),
  groups(i18nLocale),
  formatting(i18nLocale),
  labels(i18nLocale),
];

export const createViewpointMenuItems = (
  i18nLocale: I18nLocale
): ViewpointBuilderTabItemMeta[] => [
  contextComponentType(i18nLocale),
  {
    ...subgraph(i18nLocale),
    description: t(
      i18nLocale,
      'viewpointBuilder:navigation.tab.desc.createViewpointSubgraph',
      'Select reference types to add other component types to the context.'
    ),
  },
  {
    ...filters(i18nLocale),
    label: t(
      i18nLocale,
      'viewpointBuilder:navigation.tab.filterOptional',
      'Filter (optional)'
    ),
  },
  {
    ...requiredComponents(i18nLocale),
    label: t(
      i18nLocale,
      'viewpointBuilder:navigation.tab.requiredPathsOptional',
      'Required paths (optional)'
    ),
  },
  {
    ...collapsePaths(i18nLocale),
    label: t(
      i18nLocale,
      'viewpointBuilder:navigation.tab.collapsePathsOptional',
      'Collapse paths (optional)'
    ),
  },
  {
    ...groups(i18nLocale),
    label: t(
      i18nLocale,
      'viewpointBuilder:navigation.tab.groupOptional',
      'Group (optional)'
    ),
  },
  {
    ...formatting(i18nLocale),
    label: t(
      i18nLocale,
      'viewpointBuilder:navigation.tab.formatOptional',
      'Format (optional)'
    ),
  },
  {
    ...labels(i18nLocale),
    label: t(
      i18nLocale,
      'viewpointBuilder:navigation.tab.labelsOptional',
      'Label (optional)'
    ),
  },
  viewpointDetails(i18nLocale),
];

export const metamodelInformationMenuItem = (
  i18nLocale: I18nLocale
): ViewpointBuilderTabItemMeta => ({
  tabId: 'METAMODEL_TAB',
  label: t(
    i18nLocale,
    'viewpointBuilder:navigation.tab.metamodelInformation',
    'Metamodel information'
  ),
  icon: IconName.METAMODEL,
  description: '', // it's not displayed as it's represented as a button on the UI
});
